// 📁 src/LiveMap.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import socket from "./socket";
import axios from "axios";
import "./LiveMap.css";

export default function LiveMap() {
  // -----------------------------
  // Refs
  // -----------------------------
  const mapRef = useRef(null);
  const googleMap = useRef(null);

  const markersRef = useRef({}); // playerId -> marker
  const powerupMarkersRef = useRef({}); // powerupId -> marker
  const watchIdRef = useRef(null);

  const onlineSetRef = useRef(new Set()); // for immediate icon color on marker create
  const gameEndedRef = useRef(false); // winner/loser decided (single source of truth)
  const redirectingRef = useRef(false); // about to leave page
  const victoryScreenRef = useRef(false); // hard lock: ignore redirects/logout while showing win banner

  const userIdRef = useRef(null);
  const gameIdRef = useRef(null);

  const showToastTimerRef = useRef(null);

  // -----------------------------
  // State
  // -----------------------------
  const [gameInfo, setGameInfo] = useState(null); // {gameId, gameName, targetId, ...}
  const [userId, setUserId] = useState(null);

  const [players, setPlayers] = useState({}); // userId -> {username, lat, lng}
  const [myPosition, setMyPosition] = useState(null);
  const [alivePlayers, setAlivePlayers] = useState([]);

  const [myHunter, setMyHunter] = useState(null); // {hunterId, hunterName}

  const [pickupMessage, setPickupMessage] = useState("");
  const [toolboxOpen, setToolboxOpen] = useState(false);
  const [myPowerups, setMyPowerups] = useState([]); // [{id,type}]

  // ✅ map readiness (prevents “players don’t show up” when updates arrive before map init)
  const [mapReady, setMapReady] = useState(false);

  // Powerup effects (client-side display only; server enforces shield/hide)
  const BASE_KILL_RANGE_METERS = 100;
  const AUTO_COLLECT_METERS = 100;

  const [killRangeBoostUntil, setKillRangeBoostUntil] = useState(null);
  const [hideUntil, setHideUntil] = useState(null);

  const hideActive = !!hideUntil && Date.now() < hideUntil;

  const effectiveKillRange =
    killRangeBoostUntil && Date.now() < killRangeBoostUntil
      ? BASE_KILL_RANGE_METERS * 3
      : BASE_KILL_RANGE_METERS;

  // keep refs in sync (used inside stable socket handlers)
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    gameIdRef.current = gameInfo?.gameId || null;
  }, [gameInfo?.gameId]);

  // -----------------------------
  // Utilities
  // -----------------------------
  function distanceBetween(p1, p2) {
    const R = 6371e3;
    const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
    const dLng = ((p2.lng - p1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((p1.lat * Math.PI) / 180) *
        Math.cos((p2.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  const showToast = (msg, ms = 2500) => {
    setPickupMessage(msg);
    if (showToastTimerRef.current) window.clearTimeout(showToastTimerRef.current);
    showToastTimerRef.current = window.setTimeout(() => setPickupMessage(""), ms);
  };

  const myTargetId = gameInfo?.targetId || null;

  const canKill =
    myPosition &&
    myTargetId &&
    players[myTargetId] &&
    distanceBetween(myPosition, players[myTargetId]) <= effectiveKillRange;

  const targetLabel = (() => {
    if (!myTargetId) return "No target assigned yet";
    if (players[myTargetId]?.username) return players[myTargetId].username;
    return "Target assigned (waiting for GPS lock)…";
  })();
  
  const canJoinGameRoom = (gameId) => {
  if (!gameId) return false;

  // 🏆 Game already ended locally
  if (gameEndedRef.current || victoryScreenRef.current) {
    console.log("⛔ joinGameRoom blocked — local game ended");
    return false;
  }

  // 🧠 Recent game ended (cross-page protection)
  const raw = localStorage.getItem("lastGameResult");
  if (raw) {
    try {
      const last = JSON.parse(raw);
      if (last.gameId === gameId && Date.now() - last.ts < 15_000) {
        console.log("⛔ joinGameRoom blocked — cooldown active");
        return false;
      }
    } catch {}
  }

  return true;
};


  // icon helper (kept identical to your logic)
  const setMarkerIconOnline = useCallback((marker, isOnline) => {
    if (!marker) return;
    marker.setIcon({
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: isOnline ? "#1e90ff" : "#cccccc",
      fillOpacity: 1,
      strokeWeight: 1,
    });
  }, []);

  // ✅ Ensure all known players get markers once map is ready
  const ensureMarkerForPlayer = useCallback(
    (idStr, pdata) => {
      if (!googleMap.current || !mapReady) return;
      if (!pdata || typeof pdata.lat !== "number" || typeof pdata.lng !== "number") return;

      // create marker if missing
      if (!markersRef.current[idStr]) {
        const m = new window.google.maps.Marker({
          map: googleMap.current,
          position: { lat: pdata.lat, lng: pdata.lng },
          label: pdata.username || "",
        });

        const isOnline = onlineSetRef.current.has(idStr);
        setMarkerIconOnline(m, isOnline);

        markersRef.current[idStr] = m;
      }

      // update marker
      markersRef.current[idStr].setPosition({ lat: pdata.lat, lng: pdata.lng });
      if (pdata.username) markersRef.current[idStr].setLabel(pdata.username);
    },
    [mapReady, setMarkerIconOnline]
  );

  // -----------------------------
  // Hard reset on mount/unmount
  // -----------------------------
  useEffect(() => {
    googleMap.current = null;
    markersRef.current = {};
    powerupMarkersRef.current = {};
    onlineSetRef.current = new Set();
    gameEndedRef.current = false;
    redirectingRef.current = false;
    victoryScreenRef.current = false;
    setMapReady(false);

    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      Object.values(markersRef.current).forEach((m) => m?.setMap?.(null));
      Object.values(powerupMarkersRef.current).forEach((m) => m?.setMap?.(null));
      markersRef.current = {};
      powerupMarkersRef.current = {};
      if (showToastTimerRef.current) window.clearTimeout(showToastTimerRef.current);
    };
  }, []);

  // -----------------------------
  // Initial load: user + game
  // -----------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const [me, status] = await Promise.all([
          axios.get("/api/me", { withCredentials: true }),
          axios.get("/api/my-approval-status", { withCredentials: true }),
        ]);

        if (me.data?.user?.isAlive === false) {
          window.location.replace("/lobby");
          return;
        }

        const uid = me.data.user._id;
        setUserId(uid);
        setGameInfo(status.data);

        userIdRef.current = uid;
        gameIdRef.current = status.data?.gameId || null;

        // join room early (even before GPS) so we get assignments/events
        if (canJoinGameRoom(status.data?.gameId)) {
          socket.emit("joinGameRoom", status.data.gameId);
        }
      } catch (err) {
        console.error("Load error:", err);
        window.location.replace("/lobby");
      }
    };

    load();
  }, []);

  // -----------------------------
  // Rehydrate after reconnect / wake
  // (phone sleep -> killed -> should redirect)
  // -----------------------------
  useEffect(() => {
    const rehydrate = async () => {
      if (redirectingRef.current) return;
      if (victoryScreenRef.current) return;
      if (gameEndedRef.current) return;

      try {
        if (userIdRef.current) socket.emit("validateSession", { userId: userIdRef.current });

        const res = await axios.get("/api/my-approval-status", { withCredentials: true });

        if (res.data?.isAlive === false) {
          window.location.replace("/lobby");
          return;
        }

        setGameInfo(res.data);
        gameIdRef.current = res.data?.gameId || null;

        if (canJoinGameRoom(res.data?.gameId)) {
          socket.emit("joinGameRoom", res.data.gameId);
        }
      } catch (err) {
        console.error("Rehydrate failed:", err);
        window.location.replace("/lobby");
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") rehydrate();
    };

    socket.on("connect", rehydrate);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      socket.off("connect", rehydrate);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // -----------------------------
  // Session invalid => login
  // -----------------------------
  useEffect(() => {
    const onInvalid = () => {
      if (redirectingRef.current) return;
      if (victoryScreenRef.current) return;
      if (gameEndedRef.current) return;
      window.location.replace("/login");
    };

    socket.on("sessionInvalid", onInvalid);
    return () => socket.off("sessionInvalid", onInvalid);
  }, []);

  // -----------------------------
  // Force logout => login (but ignore during game over screen)
  // -----------------------------
  useEffect(() => {
    const onForceLogout = () => {
      if (redirectingRef.current) return;
      if (victoryScreenRef.current) return;
      if (gameEndedRef.current) return;
      window.location.replace("/login");
    };

    socket.on("forceLogout", onForceLogout);
    return () => socket.off("forceLogout", onForceLogout);
  }, []);

  // -----------------------------
  // Alive players HUD
  // -----------------------------
  useEffect(() => {
    const handler = (list) => {
      if (redirectingRef.current) return;
      setAlivePlayers(Array.isArray(list) ? list : []);
    };

    socket.on("alivePlayersUpdate", handler);
    return () => socket.off("alivePlayersUpdate", handler);
  }, []);

  // -----------------------------
  // Target + Hunter assignment
  // -----------------------------
  useEffect(() => {
    const onTarget = ({ targetId, targetName }) => {
      if (redirectingRef.current) return;
      setGameInfo((prev) => ({ ...(prev || {}), targetId }));
      showToast(targetName ? `🎯 Target: ${targetName}` : "🎯 Target assigned!", 2000);
    };

    const onHunter = ({ hunterId, hunterName }) => {
      if (redirectingRef.current) return;
      setMyHunter({ hunterId, hunterName });
      if (hunterName) showToast(`👀 Hunter: ${hunterName}`, 2200);
    };

    socket.on("targetAssigned", onTarget);
    socket.on("hunterAssigned", onHunter);
    socket.on("newTargetAssigned", onTarget);

    return () => {
      socket.off("targetAssigned", onTarget);
      socket.off("hunterAssigned", onHunter);
      socket.off("newTargetAssigned", onTarget);
    };
  }, []);

  // -----------------------------
  // Online/offline updates (marker coloring)
  // -----------------------------
  useEffect(() => {
    const handler = (online) => {
      if (redirectingRef.current) return;

      const ids = (online || [])
        .map((p) => p?._id?.toString())
        .filter(Boolean);

      onlineSetRef.current = new Set(ids);

      // Update existing marker icons
      Object.keys(markersRef.current).forEach((uid) => {
        const marker = markersRef.current[uid];
        if (!marker) return;
        const isOnline = onlineSetRef.current.has(uid);
        setMarkerIconOnline(marker, isOnline);
      });
    };

    socket.on("onlinePlayersUpdate", handler);
    return () => socket.off("onlinePlayersUpdate", handler);
  }, [setMarkerIconOnline]);

  // -----------------------------
  // Player movement updates (fresh + correct icon on create)
  // -----------------------------
  useEffect(() => {
    const updatePlayer = ({ userId: pid, username, lat, lng, hidden = false }) => {
      if (redirectingRef.current) return;
      if (!pid) return;

      const idStr = pid.toString();

      // Hidden => remove marker + remove from state
      if (hidden) {
        const m = markersRef.current[idStr];
        if (m) m.setMap(null);
        delete markersRef.current[idStr];

        setPlayers((prev) => {
          if (!prev[idStr]) return prev;
          const copy = { ...prev };
          delete copy[idStr];
          return copy;
        });
        return;
      }

      // ✅ IMPORTANT: Always store the update, even if map not ready yet
      setPlayers((prev) => ({
        ...prev,
        [idStr]: { username, lat, lng },
      }));

      // If map is ready, ensure marker now
      if (mapReady && googleMap.current) {
        ensureMarkerForPlayer(idStr, { username, lat, lng });
      }
    };

    socket.on("playerPositionUpdate", updatePlayer);
    return () => socket.off("playerPositionUpdate", updatePlayer);
  }, [mapReady, ensureMarkerForPlayer]);

  // ✅ When map becomes ready OR players change, ensure markers exist
  useEffect(() => {
    if (!mapReady || !googleMap.current) return;
    Object.entries(players).forEach(([idStr, pdata]) => ensureMarkerForPlayer(idStr, pdata));
  }, [mapReady, players, ensureMarkerForPlayer]);

  // -----------------------------
  // Kill events
  // -----------------------------
  useEffect(() => {
    const onKilled = ({ killerName, targetId, targetName, deathLocation }) => {
      if (redirectingRef.current) return;

      if (googleMap.current && deathLocation?.lat && deathLocation?.lng) {
        new window.google.maps.Marker({
          map: googleMap.current,
          position: deathLocation,
          label: "💀 " + (targetName || ""),
        });
      }

      if (targetId !== userIdRef.current) {
        showToast(`${killerName} eliminated ${targetName}`, 2200);
      }
    };

    socket.on("playerKilled", onKilled);
    return () => socket.off("playerKilled", onKilled);
  }, []);

  useEffect(() => {
    const onYouWereKilled = () => {
      if (redirectingRef.current) return;
      if (victoryScreenRef.current) return;
      if (gameEndedRef.current) return;

      showToast("💀 You were eliminated!", 1800);
      requestAnimationFrame(() => {
        setTimeout(() => window.location.replace("/lobby"), 1700);
      });
    };

    socket.on("youWereKilled", onYouWereKilled);
    return () => socket.off("youWereKilled", onYouWereKilled);
  }, []);

  // -----------------------------
  // Winner/Loser overlay timing + freeze logic
  // -----------------------------
  useEffect(() => {
    const handler = ({ winnerId, winnerName }) => {
      if (gameEndedRef.current) return;

      console.log("🏁 gameEnded RECEIVED", { winnerId, winnerName });

      gameEndedRef.current = true;
      victoryScreenRef.current = true;

      // stop GPS watch immediately (prevents post-game movement/rehydrate cascades)
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }

      const iWon = String(winnerId) === String(userIdRef.current);
      const duration = 10000;

      showToast(iWon ? "🏆 YOU WIN!" : `🏆 ${winnerName} won the game`, duration);

      localStorage.setItem(
        "lastGameResult",
        JSON.stringify({
          ts: Date.now(),
          winnerId,
          winnerName,
          iWon,
          gameId: gameIdRef.current,
        })
      );

      setTimeout(() => {
        redirectingRef.current = true;
        window.location.replace("/lobby");
      }, duration + 300);
    };

    socket.on("gameEnded", handler);
    return () => socket.off("gameEnded", handler);
  }, []);

  // -----------------------------
  // Shield messages (both sides)
  // -----------------------------
  useEffect(() => {
    const onShieldAbsorbed = () => showToast("🛡 Your shield absorbed the attack!", 2200);
    const onBlocked = () => showToast("🛡 Blocked! Target had a shield.", 2200);

    socket.on("shieldBlocked", onShieldAbsorbed);
    // your server currently emits "attackBlockedByShield"
    socket.on("attackBlockedByShield", onBlocked);

    return () => {
      socket.off("shieldBlocked", onShieldAbsorbed);
      socket.off("attackBlockedByShield", onBlocked);
    };
  }, []);

  // -----------------------------
  // Powerups: spawn/list/remove + pickup
  // -----------------------------
  useEffect(() => {
    const addPowerup = (p) => {
      if (redirectingRef.current) return;
      if (!googleMap.current || !mapReady) return;
      if (!p?.id) return;
      if (powerupMarkersRef.current[p.id]) return;

      powerupMarkersRef.current[p.id] = new window.google.maps.Marker({
        map: googleMap.current,
        position: { lat: p.lat, lng: p.lng },
        label: "⚡",
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: "#FFD700",
          fillOpacity: 1,
          strokeColor: "#FF8C00",
          strokeWeight: 2,
        },
      });
    };

    const handleList = (list) => {
      if (redirectingRef.current) return;
      if (!Array.isArray(list)) return;
      list.forEach(addPowerup);
    };

    const removePowerup = ({ powerupId }) => {
      const m = powerupMarkersRef.current[powerupId];
      if (m) m.setMap(null);
      delete powerupMarkersRef.current[powerupId];
    };

    const collected = ({ message, type, powerupId }) => {
      if (redirectingRef.current) return;
      setMyPowerups((prev) => [...prev, { id: powerupId, type }]);
      showToast(message || `⚡ Collected: ${String(type || "").toUpperCase()}`, 2200);
    };

    socket.on("powerupSpawned", addPowerup);
    socket.on("powerupList", handleList);
    socket.on("powerupRemoved", removePowerup);
    socket.on("powerupCollected", collected);

    return () => {
      socket.off("powerupSpawned", addPowerup);
      socket.off("powerupList", handleList);
      socket.off("powerupRemoved", removePowerup);
      socket.off("powerupCollected", collected);
    };
  }, [mapReady]);

  // -----------------------------
  // Heartbeat + GPS alive pings (for your server weak-signal logic)
  // -----------------------------
  useEffect(() => {
    if (!userId) return;

    const t = setInterval(() => {
      if (redirectingRef.current) return;
      if (socket.connected) socket.emit("heartbeat");
    }, 4000);

    return () => clearInterval(t);
  }, [userId]);

  // -----------------------------
  // GPS + map init + auto collect
  // -----------------------------
  useEffect(() => {
    if (!gameInfo?.gameId || !userId) return;
    if (redirectingRef.current) return;

    let initialized = false;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        if (redirectingRef.current) return;
        if (victoryScreenRef.current) return;
        if (gameEndedRef.current) return;

        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const myLoc = { lat, lng };
        setMyPosition(myLoc);

        // emit gps alive for weak-signal system
        socket.emit("gpsAlive");

        // init map on first fix
        if (!initialized) {
          googleMap.current = new window.google.maps.Map(mapRef.current, {
            center: myLoc,
            zoom: 19,
            mapTypeId: "terrain",
          });

          // identify + join room + request powerups
          socket.emit("identify", userId);
		  
		  if (canJoinGameRoom(gameInfo.gameId)) {
			socket.emit("joinGameRoom", gameInfo.gameId);
			socket.emit("requestPowerups");
		  } else {
			console.log("⛔ Skipping joinGameRoom during map init");
		  }

          // map ready
          setMapReady(true);

          initialized = true;
        }

        // send movement (server can hide you)
        socket.emit("playerMove", {
          userId,
          gameId: gameInfo.gameId,
          lat,
          lng,
          hidden: hideActive,
        });

        // auto-collect
        Object.entries(powerupMarkersRef.current).forEach(([powerupId, marker]) => {
          if (!marker) return;
          const p = marker.getPosition();
          if (!p) return;

          const d = distanceBetween(myLoc, { lat: p.lat(), lng: p.lng() });
          if (d <= AUTO_COLLECT_METERS) {
            socket.emit("collectPowerup", { userId, powerupId });
          }
        });
      },
      (err) => console.error("GPS error:", err),
      { enableHighAccuracy: true }
    );

    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    };
  }, [gameInfo?.gameId, userId, hideActive]);

  // -----------------------------
  // Inventory grouping (type -> count)
  // -----------------------------
  const groupedPowerups = Object.values(
    myPowerups.reduce((acc, p) => {
      if (!p?.type) return acc;
      if (!acc[p.type]) acc[p.type] = { type: p.type, count: 1 };
      else acc[p.type].count += 1;
      return acc;
    }, {})
  );

  // -----------------------------
  // Use powerups
  // -----------------------------
  const consumeOne = (type) => {
    setMyPowerups((prev) => {
      const idx = prev.findIndex((x) => x.type === type);
      if (idx === -1) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
  };

  const handleUsePowerup = (type) => {
    if (!type || redirectingRef.current) return;

    switch (type) {
      case "radius":
        setKillRangeBoostUntil(Date.now() + 30_000);
        showToast("⚡ Radius boost active!", 2200);
        consumeOne(type);
        return;

      case "shield":
        socket.emit("activateShield", { userId, gameId: gameInfo?.gameId });
        showToast("🛡 Shield activated!", 2200);
        consumeOne(type);
        return;

      case "hide":
        socket.emit("activateHide", {
          userId,
          gameId: gameInfo?.gameId,
          durationMs: 15_000,
        });
        setHideUntil(Date.now() + 15_000);
        showToast("👻 You are hidden!", 2200);
        consumeOne(type);
        return;

      default:
        showToast(`⚡ Used ${type}`, 1800);
        consumeOne(type);
        return;
    }
  };

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <div style={{ position: "relative" }}>
      {/* Toast popup */}
      {pickupMessage && (
        <div
          style={{
            position: "absolute",
            top: "60px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.8)",
            color: "lime",
            padding: "10px 18px",
            borderRadius: "8px",
            zIndex: 9999,
            fontSize: "18px",
            textAlign: "center",
            border: "2px solid lime",
            pointerEvents: "none",
          }}
        >
          {pickupMessage}
        </div>
      )}

      <div id="map-page-container">
        {/* Top HUD */}
        <div style={{ padding: "12px", background: "#111827", color: "white" }}>
          <div style={{ fontWeight: "bold" }}>
            🎮 Game: {gameInfo?.gameName || "Unknown"}
          </div>

          {/* Alive counter */}
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              background: "rgba(0,0,0,0.6)",
              padding: "6px 10px",
              color: "white",
              borderRadius: "6px",
              fontSize: "14px",
              zIndex: 9999,
            }}
          >
            🟢 {alivePlayers.length} Alive
          </div>

          {/* Hide status */}
          {hideActive && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "rgba(0,0,0,0.6)",
                padding: "6px 10px",
                borderRadius: "6px",
                fontSize: "14px",
                zIndex: 9999,
                color: "#a7f3d0",
              }}
            >
              👻 Hidden
            </div>
          )}
        </div>

        {/* Map */}
        <div id="live-map-container" ref={mapRef}></div>

        {/* Action panel */}
        <div style={{ padding: "16px", position: "relative" }}>
          <h3>
            Target: <span style={{ fontWeight: "bold" }}>{targetLabel}</span>
          </h3>

          {/* Hunter display */}
          {myHunter && !hideActive && (
            <p style={{ marginTop: "6px", color: "#f87171", fontWeight: "bold" }}>
              👀 You are being hunted by: {myHunter.hunterName}
            </p>
          )}

          {/* Toolbox button */}
          <button
            onClick={() => setToolboxOpen(true)}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "#facc15",
              borderRadius: "50%",
              width: "46px",
              height: "46px",
              fontSize: "22px",
              border: "none",
              cursor: "pointer",
              zIndex: 9999,
            }}
            title="Power-up toolbox"
          >
            ⚡
          </button>

          {/* Kill UI */}
          {myTargetId && players[myTargetId] ? (
            canKill ? (
              <button
                onClick={() =>
                  socket.emit("killPlayer", {
                    killerId: userId,
                    targetId: myTargetId,
                    gameId: gameInfo?.gameId,
                  })
                }
                style={{
                  marginTop: "12px",
                  padding: "12px 20px",
                  background: "red",
                  color: "white",
                  borderRadius: "6px",
                  fontSize: "18px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                BOMB TARGET
              </button>
            ) : (
              <p style={{ marginTop: "10px" }}>
                Move within <b>{effectiveKillRange}</b> meters to eliminate your target.
              </p>
            )
          ) : (
            <p style={{ marginTop: "10px" }}>Waiting for target assignment…</p>
          )}

          {/* Radius boost hint */}
          {killRangeBoostUntil && Date.now() < killRangeBoostUntil && (
            <p style={{ color: "lime", marginTop: "8px" }}>
              ⚡ Radius boost active! Kill range increased.
            </p>
          )}
        </div>
      </div>

      {/* Toolbox modal */}
      {toolboxOpen && (
        <div
          style={{
            position: "absolute",
            top: "20%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1f2937",
            padding: "20px",
            borderRadius: "12px",
            minWidth: "300px",
            zIndex: 10000,
            color: "white",
            border: "2px solid #facc15",
          }}
        >
          <h2 style={{ marginBottom: "12px", fontSize: "20px" }}>⚡ Power-Ups</h2>

          {groupedPowerups.length === 0 ? (
            <p>No power-ups collected yet.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {groupedPowerups.map((p) => (
                <li
                  key={p.type}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                    gap: "12px",
                  }}
                >
                  <span>
                    {String(p.type).toUpperCase()}{" "}
                    <strong style={{ color: "#facc15" }}>×{p.count}</strong>
                  </span>
                  <button
                    onClick={() => handleUsePowerup(p.type)}
                    style={{
                      background: "#10b981",
                      border: "none",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      color: "white",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Use
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            onClick={() => setToolboxOpen(false)}
            style={{
              marginTop: "14px",
              background: "#374151",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              color: "white",
              width: "100%",
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
